import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

import {
  FieldVariety,
  FieldVarietyInput,
  SimpleListResponseData,
} from "../types";
import {
  handleBasicPost,
  handleDelete,
  handleDetail,
  handleMutationError,
  handleQueryError,
  handleSave,
  handleSimpleList,
} from "./handler";
import queryClient from "./queryClient";

const methods = {
  useHarvestList: (harvestId: number) => {
    const navigate = useNavigate();
    return useQuery<SimpleListResponseData<FieldVariety>>({
      cacheTime: 0,
      queryKey: ["harvests", harvestId.toString(), "field_varieties"],
      queryFn: () =>
        handleSimpleList({ url: `harvests/${harvestId}/field_varieties` }),
      onError: handleQueryError(navigate),
    });
  },
  useDetail: (id: string | undefined) => {
    const navigate = useNavigate();
    return useQuery<FieldVariety>({
      cacheTime: 0,
      queryKey: ["field_varieties", id],
      queryFn: () => handleDetail({ baseUrl: "field_varieties", id }),
      onError: handleQueryError(navigate),
    });
  },
  useSave: (fieldVarietyInput: FieldVarietyInput) => {
    const navigate = useNavigate();
    return useMutation<FieldVariety>({
      mutationFn: () =>
        handleSave({ baseUrl: "field_varieties", input: fieldVarietyInput }),
      onError: handleQueryError(navigate),
      retry: 1,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          "harvests",
          data.harvestId.toString(),
        ]);
        await queryClient.invalidateQueries([
          "harvests",
          data.harvestId.toString(),
          "field_varieties",
        ]);
      },
    });
  },
  useSaveSimple: () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation({
      mutationFn: (fieldVarietyInput: FieldVarietyInput) =>
        handleSave({
          baseUrl: "field_varieties",
          input: fieldVarietyInput,
        }),
      onError: handleMutationError(enqueueSnackbar),
      retry: 1,
    });
  },
  useDelete: (id: number) => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation<FieldVariety>({
      mutationFn: () => handleDelete({ baseUrl: "field_varieties", id }),
      retry: 1,
      onError: handleMutationError(enqueueSnackbar),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          "harvests",
          data.harvestId.toString(),
        ]);
        await queryClient.invalidateQueries([
          "harvests",
          data.harvestId.toString(),
          "field_varieties",
        ]);
      },
    });
  },
  useDeleteImage: ({ id, imageId }: { id: number; imageId: number }) => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation<FieldVariety>({
      mutationFn: () =>
        handleBasicPost({
          baseUrl: `field_varieties/${id}/image_delete`,
          input: { imageId },
        }),
      retry: 1,
      onError: handleMutationError(enqueueSnackbar),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          "field_varieties",
          data.id.toString(),
        ]);
        await queryClient.invalidateQueries([
          "field_varieties",
          data.id.toString(),
        ]);
      },
    });
  },
};
export default methods;
